import { Cell, Label, Pie, PieChart } from "recharts";
import { type PolarViewBox } from "recharts/types/util/types";

import { ChartContainer } from "@vega-core/components/shadcn/chart";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { CustomLegend } from "./CustomLegend";

export type EmptyStatePieChartProps = {
  title?: string;
  centerLabel?: string;
  variant?: "horizontal" | "vertical";
  legendVariant?: "double" | "single";
  composition: { name?: string; displayValue?: string }[];
};

export function EmptyStatePieChart({
  title,
  centerLabel,
  variant = "vertical",
  legendVariant = "single",
  composition,
}: EmptyStatePieChartProps) {
  const isHorizontal = variant === "horizontal";

  return (
    <div className="w-full flex flex-col gap-4">
      <div
        className={cnTwMerge("p-0 md:px-4 md:pb-4", {
          "flex flex-col md:flex-row gap-4 p-0 items-center": isHorizontal,
          "flex flex-col gap-4 p-0 items-center": !isHorizontal,
        })}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-12 items-center">
            <p className="body-semi-large-semibold text-center">{title}</p>
          </div>
          <ChartContainer className="mx-auto aspect-square h-56" config={{}}>
            <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <Pie
                data={[{ value: 1 }]}
                dataKey="value"
                endAngle={-270}
                innerRadius="60%"
                outerRadius="100%"
                startAngle={90}
                strokeWidth={0}
              >
                <Cell fill="#f3f4f6" />
                <Label
                  content={({ viewBox }) => {
                    const { cx = 0, cy = 0 } = viewBox as PolarViewBox;
                    const h = 160;
                    const w = 120;

                    return (
                      <g>
                        <circle cx={cx} cy={cy} fill="transparent" r={60} />
                        <foreignObject
                          className="text-center overflow-hidden text-ellipsis text-wrap pointer-events-none"
                          height={h}
                          width={w}
                          x={cx - w / 2}
                          y={cy - h / 1.85}
                        >
                          <div className="flex flex-col items-center justify-center h-full w-full">
                            <p className="fill-text/primary heading-4">-</p>
                            <div className="w-full px-2 text-text/secondary leading-5 line-clamp-2">
                              <p className="body-small-regular break-words text-center whitespace-normal">
                                {centerLabel ?? title}
                              </p>
                            </div>
                          </div>
                        </foreignObject>
                      </g>
                    );
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
        </div>
        <div
          className={cnTwMerge(
            "grid gap-x-6 gap-y-1 w-full mt-2",
            legendVariant === "double"
              ? "grid-cols-1 sm:grid-cols-2"
              : "grid-cols-1",
          )}
        >
          {composition.map((item, index) => (
            <CustomLegend
              color="#9ca3af"
              displayValue="0%"
              key={`empty-legend-${item.name || index}`}
              name={item.name}
              shouldShowEmptyState
            />
          ))}
        </div>
      </div>
    </div>
  );
}
